import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";
import { LABELS } from "@match/shared";
import { EmptyState, PageHeader } from "@/components/Layout";
import { HoldPanel, PHASE_LABEL } from "@/components/session";
import { SessionTrade } from "@/components/trade";
import { DataTable, type DataTableColumn } from "@/components/table";
import { ErrorNote, inputClass, useCanWrite } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { keys, usePropFirms, useRefreshDirectory, useSession, useSessions } from "@/lib/queries";
import { dateTime, money } from "@/lib/format";
import type { SessionListItem } from "@/lib/types";

/*
 * La voce da cui si opera ogni giorno.
 *
 * Due parti: la LISTA delle coppie, e la POSTAZIONE che si apre premendo una
 * coppia (`?fase=<id>`). Un'operazione e' una COPPIA, quindi "a mercato" vuol
 * dire una coppia aperta, non una gamba.
 *
 * Il giorno e' quello del contatore `opsToday`: dalla mezzanotte di
 * Europe/Rome. A mezzanotte le Fasi senza operazioni tornano in "Da aprire" da
 * sole — non c'e' nessun job che le sposta, perche' non e' uno stato scritto da
 * qualche parte: e' una LETTURA di un numero che il backend ricalcola a ogni
 * richiesta.
 */

/** Le Fasi non concluse: quelle vive piu' l'attesa di payout, che non opera ma esiste. */
const OPERABILI = ["ACTIVE", "PAUSED", "AWAITING_PAYOUT"];

type Status = "DA_APRIRE" | "IN_RUN" | "CHIUSE_OGGI" | "FERME";

/**
 * Lo stato della riga, DERIVATO: nessuna colonna lo scrive, perche' una colonna
 * di stato puo' restare indietro rispetto a `openPairs`/`opsToday`.
 *
 * Una coppia a mercato ha la precedenza su tutto: una Fase con una coppia
 * aperta sta li' e basta, anche se e' in pausa o ha gia' operato oggi.
 */
function statusOf(s: SessionListItem): Status {
  if (s.summary.openPairs > 0) return "IN_RUN";
  if (s.state !== "ACTIVE") return "FERME";
  if (s.summary.opsToday > 0) return "CHIUSE_OGGI";
  return "DA_APRIRE";
}

const STATUS_META: Record<Status, { label: string; dot: string }> = {
  DA_APRIRE: { label: "Da aprire", dot: "bg-loss" },
  IN_RUN: { label: "In run", dot: "bg-warn" },
  CHIUSE_OGGI: { label: "Chiuse oggi", dot: "bg-gain" },
  FERME: { label: "Ferma", dot: "bg-blue-500" },
};

/** Il programma della Fase: quello suo, e per le Fasi di prima quello del ciclo. */
const programmaDi = (s: SessionListItem) => s.preset ?? s.cycle?.preset ?? null;

/** Il ciclo scritto per una riga: l'etichetta se c'è, altrimenti firm e preset; senza ciclo lo si dice. */
const cicloDi = (s: SessionListItem) => {
  const p = programmaDi(s);
  const nome = s.cycle?.label ?? (p ? `${p.propFirm.name} ${p.name}` : "Fase");
  return s.cycle ? nome : `${nome} · senza contabilità`;
};

type OpsBucket = "0" | "1" | "2" | "3" | "4+";
const opsBucketOf = (n: number): OpsBucket => (n >= 4 ? "4+" : (String(n) as OpsBucket));

/** La chiave con cui si ricorda la scelta Schede | Elenco fra una visita e l'altra. */
const VIEW_KEY = "match.trade.view";

export function Trade() {
  const canWrite = useCanWrite();
  const [params, setParams] = useSearchParams();
  const sessions = useSessions({ state: OPERABILI });
  const list = sessions.data ?? [];

  // La scelta sta nell'indirizzo: `/operazione?fase=<id>`.
  const requested = params.get("fase");
  const selected = list.find((s) => s.id === requested) ?? null;

  const header = <PageHeader title="Operazione" subtitle="Direzione, piano e apertura della coppia sulla fase scelta" />;

  if (sessions.isLoading) {
    return (
      <>
        {header}
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      </>
    );
  }
  if (sessions.error) {
    return (
      <>
        {header}
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(sessions.error)}</ErrorNote>
        </div>
      </>
    );
  }
  if (!list.length) {
    return (
      <>
        {header}
        <EmptyState
          title="Nessuna fase attiva"
          hint="Una fase nasce dalla scheda del cliente, col pulsante «Aggiungi conto»: torna qui per aprire la coppia."
          action={
            canWrite && (
              <Link to="/clienti" className="inline-flex rounded bg-accent px-3 py-1.5 text-sm font-medium text-on-accent hover:bg-accent/90">
                Vai ai clienti
              </Link>
            )
          }
        />
      </>
    );
  }

  if (selected) {
    return (
      <>
        {header}
        <div className="space-y-5 px-6 py-5">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <button onClick={() => setParams({}, { replace: true })} className="text-muted hover:text-ink hover:underline">
              ← Tutte le coppie
            </button>
            <span className="text-ink">{selected.client.name}</span>
            <span className="text-muted">{cicloDi(selected)}</span>
            <Link to={`/fasi/${selected.id}`} className="ml-auto text-muted hover:text-ink hover:underline">
              Dettaglio della fase
            </Link>
          </div>
          <SelectedSession id={selected.id} canWrite={canWrite} />
        </div>
      </>
    );
  }

  const apri = (id: string) => setParams({ fase: id }, { replace: true });

  return (
    <>
      {header}
      <div className="px-6 py-5">
        {/* Una Fase scelta che nel frattempo si è chiusa non sparisce in
            silenzio: si dice, o si cerca una riga che non c'è più. */}
        {requested && (
          <p className="mb-4 text-sm text-muted">
            La fase scelta non è più fra quelle su cui si opera: il suo storico resta nel{" "}
            <Link to={`/fasi/${requested}`} className="underline hover:text-ink">
              dettaglio della fase
            </Link>
            .
          </p>
        )}
        <TradeList list={list} canWrite={canWrite} onOpen={apri} />
      </div>
    </>
  );
}

function TradeList({ list, canWrite, onOpen }: { list: SessionListItem[]; canWrite: boolean; onOpen: (id: string) => void }) {
  const propFirms = usePropFirms();
  const [phase, setPhase] = useState<"TUTTE" | "PHASE_1" | "PHASE_2" | "FUNDED">("TUTTE");
  const [status, setStatus] = useState<"TUTTE" | Status>("TUTTE");
  const [firm, setFirm] = useState<"TUTTE" | string>("TUTTE");
  const [opsBucket, setOpsBucket] = useState<OpsBucket | null>(null);
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"schede" | "elenco">(() => (localStorage.getItem(VIEW_KEY) === "elenco" ? "elenco" : "schede"));

  const setViewAndRemember = (v: "schede" | "elenco") => {
    setView(v);
    localStorage.setItem(VIEW_KEY, v);
  };

  const withStatus = list.map((s) => ({ s, status: statusOf(s) }));
  const counts: Record<"TUTTE" | Status, number> = {
    TUTTE: list.length,
    DA_APRIRE: 0,
    IN_RUN: 0,
    CHIUSE_OGGI: 0,
    FERME: 0,
  };
  for (const { status: st } of withStatus) counts[st]++;

  const opsCounts: Record<OpsBucket, number> = { "0": 0, "1": 0, "2": 0, "3": 0, "4+": 0 };
  for (const s of list) opsCounts[opsBucketOf(s.summary.opsToday)]++;

  const q = query.trim().toLowerCase();
  const filtered = withStatus
    .filter(({ s, status: st }) => {
      if (phase !== "TUTTE" && s.phase !== phase) return false;
      if (status !== "TUTTE" && st !== status) return false;
      if (firm !== "TUTTE" && programmaDi(s)?.propFirm.id !== firm) return false;
      if (opsBucket && opsBucketOf(s.summary.opsToday) !== opsBucket) return false;
      if (q) {
        const haystack = [s.client.name, cicloDi(s), s.propAccount.label, s.propAccount.mt5Login, s.brokerAccount?.label ?? "", s.brokerAccount?.mt5Login ?? ""]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    })
    .map(({ s }) => s);

  const pill = (active: boolean) =>
    `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
      active ? "border-accent text-ink" : "border-line text-muted hover:text-ink"
    }`;

  const columns: DataTableColumn<SessionListItem>[] = [
    {
      key: "dot",
      header: "",
      cell: (s) => <span className={`inline-block h-2 w-2 rounded-full ${STATUS_META[statusOf(s)].dot}`} title={STATUS_META[statusOf(s)].label} aria-hidden />,
    },
    { key: "client", header: "Cliente", cell: (s) => s.client.name, sortBy: (s) => s.client.name },
    { key: "cycle", header: "Ciclo", cell: (s) => cicloDi(s), sortBy: (s) => cicloDi(s) },
    { key: "phase", header: "Fase", cell: (s) => PHASE_LABEL(s.phase), sortBy: (s) => s.phase },
    { key: "prop", header: "Prop", cell: (s) => <span className="text-prop">{s.propAccount.label}</span>, sortBy: (s) => s.propAccount.label },
    { key: "broker", header: "Broker", cell: (s) => <span className="text-broker">{s.brokerAccount?.label ?? "—"}</span>, sortBy: (s) => s.brokerAccount?.label ?? "" },
    { key: "symbol", header: "Simbolo", cell: (s) => s.symbols.prop ?? s.canonicalSymbol },
    { key: "today", header: "Oggi", align: "right", cell: (s) => s.summary.opsToday, sortBy: (s) => s.summary.opsToday },
    { key: "menu", header: "", cell: (s) => <FaseMenu s={s} canWrite={canWrite} /> },
  ];

  return (
    <div className="space-y-4">
      <div role="tablist" aria-label="Fase del ciclo" className="inline-flex overflow-hidden rounded border border-line text-sm">
        {(["TUTTE", "PHASE_1", "PHASE_2", "FUNDED"] as const).map((p) => (
          <button
            key={p}
            role="tab"
            aria-selected={phase === p}
            onClick={() => setPhase(p)}
            className={`px-3 py-1.5 ${phase === p ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
          >
            {p === "TUTTE" ? "Tutte" : PHASE_LABEL(p)}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(["TUTTE", "DA_APRIRE", "IN_RUN", "CHIUSE_OGGI", "FERME"] as const).map((k) => (
          <button key={k} onClick={() => setStatus(k)} className={pill(status === k)}>
            {k !== "TUTTE" && <span className={`h-1.5 w-1.5 rounded-full ${STATUS_META[k].dot}`} aria-hidden />}
            {k === "TUTTE" ? "Tutte" : STATUS_META[k].label}
            <span className="text-faint">{counts[k]}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setFirm("TUTTE")} className={pill(firm === "TUTTE")}>
          Tutte le prop firm
        </button>
        {(propFirms.data ?? []).map((f) => (
          <button key={f.id} onClick={() => setFirm(f.id)} className={pill(firm === f.id)}>
            {f.name}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-faint">Operazioni di oggi</span>
        {(["0", "1", "2", "3", "4+"] as const).map((b) => (
          <button key={b} onClick={() => setOpsBucket((cur) => (cur === b ? null : b))} className={pill(opsBucket === b)}>
            {b} <span className="text-faint">{opsCounts[b]}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca cliente, ciclo o conto"
          aria-label="Cerca cliente, ciclo o conto"
          className={`${inputClass} max-w-xs`}
        />
        <div className="ml-auto inline-flex overflow-hidden rounded border border-line text-sm" role="tablist" aria-label="Vista">
          <button
            role="tab"
            aria-selected={view === "schede"}
            onClick={() => setViewAndRemember("schede")}
            className={`px-3 py-1.5 ${view === "schede" ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
          >
            Schede
          </button>
          <button
            role="tab"
            aria-selected={view === "elenco"}
            onClick={() => setViewAndRemember("elenco")}
            className={`px-3 py-1.5 ${view === "elenco" ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
          >
            Elenco
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="Nessuna coppia con questi filtri" hint="Cambia i filtri sopra, o cerca un altro nome." />
      ) : view === "schede" ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((s) => (
            <SessionCard key={s.id} s={s} canWrite={canWrite} onOpen={onOpen} />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(s) => s.id}
          onRowClick={(s) => onOpen(s.id)}
          initialSort={{ key: "client", dir: "asc" }}
          empty={{ title: "Nessuna coppia con questi filtri", hint: "Cambia i filtri sopra, o cerca un altro nome." }}
        />
      )}
    </div>
  );
}

/**
 * L'EQUITY DELLA CARD: L'ULTIMO VALORE LETTO E SALVATO, mai una lettura di adesso
 * (09/10). La pagina Operazione non collega i conti e non chiede niente al
 * gateway: il numero e' `lastEquity`, scritto da connessione, sincronizzazione
 * e chiusure. Da collegato puo' avere qualche minuto, e lo dice il titolo; da
 * scollegato e' in grigio con l'ora della lettura, perche' e' una fotografia.
 */
function LastEquity({ account }: { account: { lastEquity: string | null; lastSyncAt: string | null; currency: string; isConnected: boolean } }) {
  if (account.lastEquity === null) {
    return <p className="text-faint" title="Equity mai letta: si legge alla prima connessione del conto">Equity —</p>;
  }
  const quando = account.lastSyncAt ? new Date(account.lastSyncAt) : null;
  const oggi = quando && quando.toDateString() === new Date().toDateString();
  const ora = quando
    ? oggi
      ? quando.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
      : dateTime(quando)
    : null;
  const titolo = `Ultima equity letta e salvata${quando ? ` il ${quando.toLocaleString("it-IT")}` : ""}: non è una lettura di adesso.`;
  return account.isConnected ? (
    <p className="tabular-nums text-ink" title={titolo}>
      {money(account.lastEquity, account.currency)}
    </p>
  ) : (
    <p className="tabular-nums text-faint" title={`${titolo} Il conto è scollegato.`}>
      {money(account.lastEquity, account.currency)}
      {ora && <span className="ml-1">· letta {oggi ? "alle " : "il "}{ora}</span>}
    </p>
  );
}

function SessionCard({ s, canWrite, onOpen }: { s: SessionListItem; canWrite: boolean; onOpen: (id: string) => void }) {
  const meta = STATUS_META[statusOf(s)];
  const simbolo = s.symbols.prop ?? s.canonicalSymbol;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(s.id)}
      onKeyDown={(e) => e.key === "Enter" && onOpen(s.id)}
      className="card flex cursor-pointer flex-col gap-2 p-4 text-sm hover:border-muted/60"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5">
          <span className={`h-2 w-2 shrink-0 rounded-full ${meta.dot}`} aria-hidden />
          <span className="text-xs text-muted">{meta.label}</span>
        </span>
        <FaseMenu s={s} canWrite={canWrite} />
      </div>
      <div className="min-w-0">
        <p className="truncate font-medium text-ink">{s.client.name}</p>
        <p className="truncate text-xs text-muted">
          {cicloDi(s)} · {PHASE_LABEL(s.phase)}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-x-3 text-xs">
        <div className="min-w-0">
          <p className="truncate font-medium text-prop">{s.propAccount.label}</p>
          <p className="machine-id truncate">{s.propAccount.mt5Login}</p>
          <LastEquity account={s.propAccount} />
        </div>
        <div className="min-w-0">
          <p className="truncate font-medium text-broker">{s.brokerAccount?.label ?? "in attesa del conto"}</p>
          <p className="machine-id truncate">{s.brokerAccount?.mt5Login ?? "—"}</p>
          {s.brokerAccount && <LastEquity account={s.brokerAccount} />}
        </div>
      </div>
      <div className="mt-auto flex items-center justify-between text-xs text-muted">
        <span title={`Prop ${s.symbols.prop ?? "non mappato"} contro broker ${s.symbols.broker ?? "non mappato"}`}>{simbolo}</span>
        <span title="Coppie con almeno un ticket dalla mezzanotte di oggi (Europe/Rome)">Oggi: {s.summary.opsToday}</span>
      </div>
    </div>
  );
}

/**
 * Il menu "…" DELLA LISTA: pausa/riprendi e il link al dettaglio.
 *
 * Fase superata / Conto bruciato / payout non ci sono: chiedono dati che
 * `/sessions` (l'elenco) non porta — `preset.phases`, il P&L del broker, gli
 * eventuali payout — e caricarli riga per riga per un menu vorrebbe dire una
 * chiamata in piu' per ogni coppia in lista. Restano dove i dati ci sono gia':
 * nel dettaglio della fase.
 */
function FaseMenu({ s, canWrite }: { s: SessionListItem; canWrite: boolean }) {
  const refresh = useRefreshDirectory();
  const [open, setOpen] = useState(false);
  const [holdPanel, setHoldPanel] = useState(false);
  const [busy, setBusy] = useState(false);

  const riprendi = async () => {
    setBusy(true);
    try {
      await api.sessions.resume(s.id);
      await refresh();
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <span className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-label="Azioni della fase"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="rounded p-1 text-muted hover:bg-raised hover:text-ink"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-10 mt-1 w-48 rounded border border-line bg-surface py-1 text-sm shadow-lg">
          {canWrite &&
            (s.state === "PAUSED" ? (
              <button role="menuitem" disabled={busy} onClick={() => void riprendi()} className="block w-full px-3 py-1.5 text-left hover:bg-raised disabled:opacity-50">
                {busy ? "Ripresa…" : "Riprendi"}
              </button>
            ) : (
              <button
                role="menuitem"
                onClick={() => {
                  setHoldPanel(true);
                  setOpen(false);
                }}
                className="block w-full px-3 py-1.5 text-left hover:bg-raised"
              >
                Pausa
              </button>
            ))}
          <Link role="menuitem" to={`/fasi/${s.id}`} onClick={() => setOpen(false)} className="block w-full px-3 py-1.5 text-left hover:bg-raised">
            Dettaglio della fase
          </Link>
        </div>
      )}
      {holdPanel && <HoldPanel sessionId={s.id} onClose={() => setHoldPanel(false)} />}
    </span>
  );
}

/**
 * Pagina aperta = attivita': un segnale subito e poi ogni 60 s, per i due conti
 * della Fase: li tiene fuori dallo scollegamento dei conti inattivi. **Non li
 * ricollega**: la pagina Operazione non collega mai un conto (dal 952762e il
 * backend risponde solo `noteActivity`), si collega con «Connetti». Chiusa la
 * pagina, il segnale smette.
 */
function useActivityHeartbeat(id: string, enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    let viva = true;
    const battito = () =>
      api.sessions.activity(id).then(
        (r) => {
          // Ricollegato, o appena fallito (il conto diventa rosso col motivo): si rilegge la Fase.
          if (viva && r.accounts.some((a) => a.reconnected || (a.suspended && !a.connected))) {
            void qc.invalidateQueries({ queryKey: keys.sessions });
          }
        },
        () => undefined,
      );
    void battito();
    const timer = setInterval(() => void battito(), 60_000);
    return () => {
      viva = false;
      clearInterval(timer);
    };
  }, [id, enabled, qc]);
}

function SelectedSession({ id, canWrite }: { id: string; canWrite: boolean }) {
  const session = useSession(id);
  useActivityHeartbeat(id, canWrite);
  if (session.isLoading) return <p className="text-sm text-muted">Caricamento della fase…</p>;
  if (session.error || !session.data) return <ErrorNote>{session.error ? errorMessage(session.error) : "Fase non trovata"}</ErrorNote>;

  const s = session.data;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted">
        <span>
          {LABELS.sessionState[s.state]} · Predefinito: {s.symbols?.prop.brokerSymbol ?? s.canonicalSymbol} contro{" "}
          {s.symbols?.broker.brokerSymbol ?? s.canonicalSymbol} · oggi: {s.summary.opsToday}{" "}
          {s.summary.opsToday === 1 ? "operazione" : "operazioni"}
        </span>
      </div>
      <SessionTrade s={s} canWrite={canWrite} />
    </div>
  );
}
